import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import axios from 'axios'
import { toast } from 'react-toastify'
import config from '../../config'

// [field, label]
const TEXT_FIELDS = [
  ['CurrentAreaCASqft', 'Curr CA SQFT'],
  ['EligibleAreaCAPlus50PercentSqft', 'Eligible CA area'],
  ['NearestAreaAsPerPlanSqft', 'Area As Per Plan'],
  ['FlatSizeOptedAsPerScheme', 'Area Opted As Per Sch'],
  ['DifferenceInAreaSqft6Minus5', 'Diff In Area'],
  ['AdditionalAreaAsPerSchemeI7Minus5', 'Add Area Sch (i)'],
  ['JodiFlat', 'Jodi flat'],
  ['ExtraFlatSchemeII', 'EXTRA FLAT SCH II']
]

const toForm = raw => ({
  MembersName: raw?.MembersName || '',
  BldgNo: raw?.BldgNo != null ? String(raw.BldgNo) : '',
  FlatNo: raw?.FlatNo != null ? String(raw.FlatNo) : '',
  UserId: raw?.UserId != null ? String(raw.UserId) : '',
  ...Object.fromEntries(TEXT_FIELDS.map(([f]) => [f, raw?.[f] != null ? String(raw[f]) : '']))
})

// Admin dialog: edit every field of one unit plan (PUT /flat-details/:id).
// `raw` is the server record for the row (as returned by /flat-details/getAll).
const Editunitplanmodal = ({ show, raw, onClose, onSaved }) => {
  const [form, setForm] = useState(toForm(raw))
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (show) {
      setForm(toForm(raw))
      setErrors({})
    }
  }, [show, raw])

  const set = field => e => {
    const value = e.target.value
    setForm(prev => ({ ...prev, [field]: value }))
    setErrors(prev => ({ ...prev, [field]: undefined }))
  }

  const validate = () => {
    const e = {}
    if (!form.MembersName.trim()) e.MembersName = 'Member name is required'
    ;['BldgNo', 'FlatNo'].forEach(k => {
      const v = Number(form[k])
      if (!form[k].trim() || !Number.isInteger(v) || v < 1 || v > 9999) e[k] = 'Enter a whole number, 1 or more'
    })
    if (form.UserId.trim() && (!Number.isInteger(Number(form.UserId)) || Number(form.UserId) < 1)) {
      e.UserId = 'Enter a user number, or leave empty'
    }
    TEXT_FIELDS.forEach(([f]) => {
      if (form[f].length > 255) e[f] = 'Too long (max 255 characters)'
    })
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const submit = async ev => {
    ev.preventDefault()
    if (!raw || !validate()) return
    setSaving(true)
    try {
      const payload = {
        MembersName: form.MembersName.trim(),
        BldgNo: Number(form.BldgNo),
        FlatNo: Number(form.FlatNo),
        UserId: form.UserId.trim() === '' ? null : Number(form.UserId),
        ...Object.fromEntries(TEXT_FIELDS.map(([f]) => [f, form[f].trim()]))
      }
      const res = await axios.put(`${config.apiUrl}/flat-details/${raw.ID}`, payload, {
        headers: { Authorization: localStorage.getItem('token') }
      })
      toast.success('Unit plan updated')
      onSaved(res.data?.data)
      onClose()
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update unit plan'
      // show the server's complaint next to the field it is about
      if (err.response?.status === 409) setErrors(prev => ({ ...prev, FlatNo: msg }))
      else if (/UserId/i.test(msg)) setErrors(prev => ({ ...prev, UserId: msg }))
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const field = (name, label, input, hint) => (
    <div className="mb-3">
      <label className="form-label" htmlFor={`edit-flat-${name}`}>{label}</label>
      {input}
      {hint && <div className="form-text">{hint}</div>}
      {errors[name] && <div className="invalid-feedback d-block">{errors[name]}</div>}
    </div>
  )

  const text = (name, type = 'text') => (
    <input
      id={`edit-flat-${name}`}
      type={type}
      inputMode={type === 'number' ? 'numeric' : undefined}
      min={type === 'number' ? '1' : undefined}
      className={`form-control${errors[name] ? ' is-invalid' : ''}`}
      value={form[name]}
      onChange={set(name)}
    />
  )

  return (
    <Modal show={show} onHide={saving ? () => {} : onClose} centered scrollable backdrop="static" aria-labelledby="edit-flat-title">
      <form onSubmit={submit} noValidate>
        <Modal.Header closeButton closeLabel="Close">
          <Modal.Title as="h2" id="edit-flat-title" className="h3 mb-0">Edit unit plan</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {field('MembersName', <>Member name <span className="text-danger" aria-hidden="true">*</span></>, text('MembersName'))}

          <div className="row g-3">
            <div className="col-sm-6">{field('BldgNo', 'Bldg no.', text('BldgNo', 'number'))}</div>
            <div className="col-sm-6">{field('FlatNo', 'Flat no.', text('FlatNo', 'number'))}</div>
          </div>

          {TEXT_FIELDS.map(([f, label]) => (
            <React.Fragment key={f}>{field(f, label, text(f))}</React.Fragment>
          ))}

          {field(
            'UserId',
            'Linked user ID',
            text('UserId', 'number'),
            'The login user who owns this flat. Members see this flat and its payments through this link.'
          )}
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving && <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default Editunitplanmodal
